// ShareCardMaker — "Make your card" on a referrer's share page (job
// mct_7e138a6a-e47; design docs/plans/event-share-card-generator.md in
// tsb-programs-command-centre; Michael's yes on mca_ccfca4af3224).
//
// The worker decides which card this referrer gets (speaking, attending or
// the general promoting card) and hands over Command Centre's pack: one
// finished background per size plus where the photo, name and firm go. This
// draws each size at its exact pixels on a canvas, in the person's own
// browser, and exports a PNG.
//
// ★ THE PHOTO NEVER LEAVES THE DEVICE. A picked photo is read into the page,
//   drawn and dropped with the tab; nothing is uploaded or stored. A
//   speaker's lineup headshot comes from the worker (same-origin bytes, so the
//   canvas can export it).
// ★ NO LINK ON THE CARD. The link travels in the post: the phone share sheet
//   gets the chosen post's short version with the referrer's own link.
// ★ Without a photo the card carries no name, as Michael approved.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  fetchShareCardPhoto, postShareCardEvent, type ShareCardRole, type ShareCards, type ShareCardTextSlot, type ShareCardVariant,
} from '../api';
import { DEFAULT_CROP, MAX_ZOOM, cardFileName, clampCrop, fitFontPx, photoRect, zipStore, type PhotoCrop } from '../lib/shareCards';

const SIZE_LABEL: Record<ShareCardVariant['size'], string> = {
  square: 'Square', portrait: 'Portrait', story: 'Story', landscape: 'Landscape', email: 'Email header',
};
const LEAD: Record<ShareCardRole, string> = {
  speaking: 'Make your own “I’m speaking at” card to post with your link.',
  attending: 'Make your own “I’m attending” card to post with your link.',
  promoting: 'Download a card to post with your link.',
};

const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '22px 22px 24px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)', marginBottom: 18 };
const small: CSSProperties = { fontSize: 14, color: '#6b6b80', lineHeight: 1.5 };
const label: CSSProperties = { display: 'block', fontSize: 14, fontWeight: 700, color: '#0A0A2E', margin: '12px 0 4px' };
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '10px 12px', border: '1px solid #d4d4dc', borderRadius: 8 };
const warn: CSSProperties = { fontSize: 14, color: '#991B1B', margin: '6px 0 0' };

function loadImage(src: string, cors: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (cors) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load ${src}`));
    img.src = src;
  });
}

const fontOf = (px: number, weight: number) => `${weight} ${px}px Poppins, sans-serif`;

/** Draw one card. Returns which lines would not fit at their floor size. */
function drawCard(
  canvas: HTMLCanvasElement, v: ShareCardVariant, bg: HTMLImageElement,
  photo: HTMLImageElement | null, crop: PhotoCrop, name: string, firm: string,
): { name: boolean; firm: boolean } {
  const tooLong = { name: false, firm: false };
  if (canvas.width !== v.width) canvas.width = v.width;
  if (canvas.height !== v.height) canvas.height = v.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return tooLong;
  ctx.clearRect(0, 0, v.width, v.height);
  ctx.drawImage(bg, 0, 0, v.width, v.height);
  if (!v.photo || !v.slots || !photo) return tooLong;
  const { cx, cy, r } = v.slots.photo;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();
  const at = photoRect(crop, photo.naturalWidth, photo.naturalHeight, cx, cy, r);
  ctx.drawImage(photo, at.x, at.y, at.w, at.h);
  ctx.restore();
  const line = (slot: ShareCardTextSlot, text: string): boolean => {
    const t = text.trim();
    if (!t) return true;
    const px = fitFontPx(t, slot, (n) => { ctx.font = fontOf(n, slot.weight); return ctx.measureText(t).width; });
    if (px == null) return false;
    ctx.font = fontOf(px, slot.weight);
    ctx.fillStyle = slot.colour;
    ctx.textAlign = slot.align;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(t, slot.x, slot.y);
    return true;
  };
  tooLong.name = !line(v.slots.name, name);
  tooLong.firm = !line(v.slots.firm, firm);
  return tooLong;
}

const toBlob = (c: HTMLCanvasElement) => new Promise<Blob | null>((resolve) => c.toBlob(resolve, 'image/png'));

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Phones that can hand a file to the share sheet (iPhone Safari, Android
 *  Chrome). Desktop browsers mostly cannot, and get the plain note instead. */
function canShareFiles(): boolean {
  try {
    const probe = new File([new Uint8Array(1)], 'card.png', { type: 'image/png' });
    return typeof navigator.share === 'function' && !!navigator.canShare?.({ files: [probe] });
  } catch {
    return false;
  }
}

/** Browsers that can put a picture on the clipboard, so a card can be pasted
 *  into a LinkedIn or Facebook post that a link alone cannot attach it to. */
function canCopyImage(): boolean {
  try {
    return typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard?.write === 'function';
  } catch {
    return false;
  }
}

export default function ShareCardMaker({ token, cards, shareText }: { token: string; cards: ShareCards; shareText: string }) {
  const [bgs, setBgs] = useState<Map<string, HTMLImageElement> | null>(null);
  const [failed, setFailed] = useState(false);
  const [photo, setPhoto] = useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<PhotoCrop>(DEFAULT_CROP);
  const [name, setName] = useState(cards.name);
  const [firm, setFirm] = useState(cards.firm);
  const [tooLong, setTooLong] = useState({ name: false, firm: false });
  const [busy, setBusy] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState('');
  const canvases = useRef<Record<string, HTMLCanvasElement | null>>({});
  const blobs = useRef<Record<string, Blob>>({});
  const blobGen = useRef(0);
  const madeCounted = useRef(false);
  const photoUrl = useRef<string | null>(null);
  const cropBox = useRef<HTMLCanvasElement | null>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);
  const share = useMemo(canShareFiles, []);
  const canCopy = useMemo(canCopyImage, []);
  const [copied, setCopied] = useState<string | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);

  const photoCards = cards.withPhoto.length > 0;
  const usePhoto = photoCards && !!photo;
  const variants = usePhoto ? cards.withPhoto : cards.noPhoto;

  // The backgrounds (both sets) and the fonts, once.
  useEffect(() => {
    let alive = true;
    const all = [...cards.withPhoto, ...cards.noPhoto];
    const fonts = [...new Set(cards.withPhoto.flatMap((v) => (v.slots ? [v.slots.name.weight, v.slots.firm.weight] : [])))]
      .map((w) => document.fonts?.load(fontOf(40, w)).catch(() => []) ?? Promise.resolve([]));
    Promise.all([Promise.all(all.map((v) => loadImage(v.background, true).then((img) => [v.background, img] as const))), ...fonts])
      .then(([pairs]) => { if (alive) setBgs(new Map(pairs)); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [cards]);

  // A speaker starts with their lineup headshot.
  useEffect(() => {
    if (!cards.photo || !photoCards) return;
    let alive = true;
    fetchShareCardPhoto(token).then(async (blob) => {
      if (!alive || !blob) return;
      const url = URL.createObjectURL(blob);
      try {
        const img = await loadImage(url, false);
        if (!alive) { URL.revokeObjectURL(url); return; }
        photoUrl.current = url;
        setPhoto(img);
      } catch {
        URL.revokeObjectURL(url);
      }
    });
    return () => { alive = false; };
  }, [token, cards.photo, photoCards]);

  useEffect(() => () => { if (photoUrl.current) URL.revokeObjectURL(photoUrl.current); }, []);

  // Draw every size, a beat after the last change.
  useEffect(() => {
    if (!bgs) return;
    const t = setTimeout(() => {
      const worst = { name: false, firm: false };
      const gen = ++blobGen.current;
      blobs.current = {};
      for (const v of variants) {
        const c = canvases.current[v.size];
        const bg = bgs.get(v.background);
        if (!c || !bg) continue;
        const r = drawCard(c, v, bg, usePhoto ? photo : null, crop, name, firm);
        worst.name ||= r.name;
        worst.firm ||= r.firm;
        // Ready ahead of a tap, so the phone share sheet opens inside the tap.
        const size = v.size;
        void toBlob(c).then((b) => { if (b && gen === blobGen.current) blobs.current[size] = b; });
      }
      setTooLong(worst);
    }, 80);
    return () => clearTimeout(t);
  }, [bgs, variants, usePhoto, photo, crop, name, firm]);

  // The crop circle.
  useEffect(() => {
    const c = cropBox.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx || !photo) return;
    const d = c.width;
    ctx.clearRect(0, 0, d, d);
    ctx.save();
    ctx.beginPath();
    ctx.arc(d / 2, d / 2, d / 2, 0, Math.PI * 2);
    ctx.clip();
    const at = photoRect(crop, photo.naturalWidth, photo.naturalHeight, d / 2, d / 2, d / 2);
    ctx.drawImage(photo, at.x, at.y, at.w, at.h);
    ctx.restore();
  }, [photo, crop]);

  function choosePhoto(file: File | undefined) {
    setPhotoError('');
    if (!file) return;
    if (!file.type.startsWith('image/')) { setPhotoError('That file is not a picture. Choose a photo.'); return; }
    const url = URL.createObjectURL(file);
    loadImage(url, false).then((img) => {
      if (photoUrl.current) URL.revokeObjectURL(photoUrl.current);
      photoUrl.current = url;
      setCrop(DEFAULT_CROP);
      setPhoto(img);
    }).catch(() => {
      URL.revokeObjectURL(url);
      setPhotoError('That photo would not open. Try a JPEG or PNG.');
    });
  }

  function removePhoto() {
    if (photoUrl.current) URL.revokeObjectURL(photoUrl.current);
    photoUrl.current = null;
    setPhoto(null);
    setCrop(DEFAULT_CROP);
  }

  function nudge(dx: number, dy: number) {
    if (!photo || !cropBox.current) return;
    const d = cropBox.current.getBoundingClientRect().width || cropBox.current.width;
    setCrop((c) => clampCrop({ ...c, x: c.x + dx / d, y: c.y + dy / d }, photo.naturalWidth, photo.naturalHeight));
  }

  function counted(action: 'downloaded' | 'shared') {
    if (!madeCounted.current) { madeCounted.current = true; postShareCardEvent(token, 'made', cards.role); }
    postShareCardEvent(token, action, cards.role);
  }

  const blocked = tooLong.name || tooLong.firm;
  const fileOf = (v: ShareCardVariant) => cardFileName(cards.version, `${cards.role}${usePhoto ? '' : '-plain'}`, v.size);

  async function blobFor(v: ShareCardVariant): Promise<Blob | null> {
    const c = canvases.current[v.size];
    return blobs.current[v.size] ?? (c ? toBlob(c) : null);
  }

  async function download(v: ShareCardVariant) {
    setBusy(v.size);
    try {
      const b = await blobFor(v);
      if (b) { saveBlob(b, fileOf(v)); counted('downloaded'); }
    } finally { setBusy(null); }
  }

  async function downloadAll() {
    setBusy('all');
    try {
      const files: Array<{ name: string; bytes: Uint8Array }> = [];
      for (const v of variants) {
        const b = await blobFor(v);
        if (b) files.push({ name: fileOf(v), bytes: new Uint8Array(await b.arrayBuffer()) });
      }
      if (files.length) {
        saveBlob(new Blob([zipStore(files)], { type: 'application/zip' }), `${cardFileName(cards.version, cards.role, 'cards').replace(/\.png$/, '')}.zip`);
        counted('downloaded');
      }
    } finally { setBusy(null); }
  }

  async function copyCard(v: ShareCardVariant) {
    setBusy(v.size);
    try {
      // The blob goes in as a promise so Safari still sees the click that asked for it.
      await navigator.clipboard.write([new ClipboardItem({
        'image/png': blobFor(v).then((b) => { if (!b) throw new Error('card not ready'); return b; }),
      })]);
      counted('downloaded');
      setCopyFailed(false);
      setCopied(v.size);
      setTimeout(() => setCopied((s) => (s === v.size ? null : s)), 3000);
    } catch {
      setCopyFailed(true);
    } finally { setBusy(null); }
  }

  async function shareCard(v: ShareCardVariant) {
    const b = blobs.current[v.size] ?? await blobFor(v);
    if (!b) return;
    try {
      await navigator.share({ files: [new File([b], fileOf(v), { type: 'image/png' })], text: shareText });
      counted('shared');
    } catch { /* closed the sheet, or the browser refused: nothing to do */ }
  }

  if (failed) {
    return (
      <div style={card}>
        <p style={{ margin: '0 0 6px', fontWeight: 700 }}>Make your card</p>
        <p style={{ ...small, margin: 0 }}>The cards did not load just now. Reload the page to try again.</p>
      </div>
    );
  }

  return (
    <div style={card}>
      <p style={{ margin: '0 0 6px', fontWeight: 700 }}>Make your card</p>
      <p style={{ ...small, margin: '0 0 12px' }}>{LEAD[cards.role]}</p>

      {photoCards && (
        <div style={{ marginBottom: 6 }}>
          {photo ? (
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <canvas
                ref={cropBox} width={220} height={220} aria-label="Your photo. Drag to move it."
                style={{ width: 160, height: 160, borderRadius: '50%', touchAction: 'none', cursor: 'grab', background: '#eef0f6' }}
                onPointerDown={(e) => { drag.current = { x: e.clientX, y: e.clientY }; e.currentTarget.setPointerCapture(e.pointerId); }}
                onPointerMove={(e) => {
                  if (!drag.current) return;
                  nudge(e.clientX - drag.current.x, e.clientY - drag.current.y);
                  drag.current = { x: e.clientX, y: e.clientY };
                }}
                onPointerUp={() => { drag.current = null; }}
                onPointerCancel={() => { drag.current = null; }}
              />
              <div style={{ flex: '1 1 180px' }}>
                <label style={{ ...label, marginTop: 0 }} htmlFor="card-zoom">Zoom</label>
                <input id="card-zoom" type="range" min={1} max={MAX_ZOOM} step={0.01} value={crop.zoom} style={{ width: '100%' }}
                  onChange={(e) => setCrop((c) => clampCrop({ ...c, zoom: Number(e.target.value) }, photo.naturalWidth, photo.naturalHeight))} />
                <p style={{ ...small, margin: '4px 0 8px' }}>Drag the photo to move it.</p>
                <label className="btn btn-secondary" style={{ display: 'inline-block', marginRight: 8, cursor: 'pointer' }}>
                  Change photo
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { choosePhoto(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
                <button type="button" className="btn btn-secondary" onClick={removePhoto}>No photo</button>
              </div>
            </div>
          ) : (
            <label className="btn btn-secondary" style={{ display: 'inline-block', cursor: 'pointer' }}>
              Add your photo
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { choosePhoto(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
          )}
          {photoError && <p style={warn}>{photoError}</p>}
          <p style={{ ...small, margin: '8px 0 0' }}>Your photo stays on your device. It is never uploaded.</p>
        </div>
      )}

      {usePhoto ? (
        <div style={{ marginBottom: 14 }}>
          <label style={label} htmlFor="card-name">Your name</label>
          <input id="card-name" style={input} value={name} maxLength={cards.limits.name} onChange={(e) => setName(e.target.value)} />
          {tooLong.name && <p style={warn}>That name is too long for the card. Try a shorter version.</p>}
          <label style={label} htmlFor="card-firm">Your firm</label>
          <input id="card-firm" style={input} value={firm} maxLength={cards.limits.firm} onChange={(e) => setFirm(e.target.value)} />
          {tooLong.firm && <p style={warn}>That is too long for the card. Try a shorter version.</p>}
        </div>
      ) : (
        photoCards && <p style={{ ...small, margin: '8px 0 14px' }}>Without a photo, the card carries no name.</p>
      )}

      {!bgs && <p style={small}>Getting your cards ready…</p>}
      <div style={{ display: bgs ? 'grid' : 'none', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 14 }}>
        {variants.map((v) => (
          <div key={`${usePhoto}-${v.size}`}>
            <canvas
              ref={(el) => { canvases.current[v.size] = el; }}
              width={v.width} height={v.height} aria-label={`${SIZE_LABEL[v.size]} card preview`}
              style={{ width: '100%', height: 'auto', aspectRatio: `${v.width} / ${v.height}`, borderRadius: 8, border: '1px solid #e5e7eb', display: 'block' }}
            />
            <span style={{ ...small, display: 'block', margin: '6px 0' }}>{SIZE_LABEL[v.size]} · {v.width}×{v.height}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button type="button" className="btn btn-secondary" disabled={blocked || !!busy} onClick={() => download(v)}>
                {busy === v.size ? 'Saving…' : 'Download'}
              </button>
              {share && (
                <button type="button" className="btn btn-secondary" disabled={blocked || !!busy} onClick={() => shareCard(v)}>Share</button>
              )}
              {canCopy && (
                <button type="button" className="btn btn-secondary" disabled={blocked || !!busy} onClick={() => copyCard(v)}>
                  {copied === v.size ? 'Copied' : 'Copy image'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {copyFailed && <p style={warn}>Your browser would not copy the picture. Use Download instead, then attach it to your post.</p>}
      {bgs && (
        <div style={{ marginTop: 14 }}>
          <button type="button" className="btn btn-secondary" disabled={blocked || !!busy} onClick={downloadAll}>
            {busy === 'all' ? 'Saving…' : 'Download all sizes'}
          </button>
        </div>
      )}
      <p style={{ ...small, margin: '14px 0 0' }}>
        {share
          ? 'Share opens your phone’s share sheet with your card and the post above, your link included.'
          : canCopy
            ? 'Copy your card, open a post with the buttons above, then paste the picture into it. LinkedIn, Facebook and X don’t let a link carry a picture, so the paste is the one step left to you. Download works too.'
            : 'Download your card, then attach it to your post. The buttons above open the post with your link, but LinkedIn, Facebook and X don’t let a link carry a picture.'}
      </p>
    </div>
  );
}
